import classNames from 'classnames';
import { components } from 'react-select';
import { makeStyles } from '@material-ui/core';
import TagHash from '../../tagHash';
import ColorBox from '../../colorBox';
import UserAvatar from '../../userAvatar';
import Tooltip from '../../tooltip';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import { ReactComponent as TextReplyIcon } from '../../../../assets/icons/text_snippet.svg';
import { ReactComponent as PersonalAttributeIcon } from '../../../../assets/icons/private-attribute-icon.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-dark.svg';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    borderRadius: 4,
    fontFamily: 'Proximanova-Regular',
    fontSize: '16px',
    lineHeight: '20px',
    color: primary.bluish1,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    height: 40,
    padding: spacing(2),
    width: '100%',
  },
  disabled: {
    color: primary.bluish7,
    cursor: 'default',
  },
  disabledLabel: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  optionText: {
    fontFamily: 'Proximanova-Regular',
    fontSize: '16px',
    lineHeight: '20px',
    color: primary.bluish1,
  },
  selected: {
    backgroundColor: primary.bluish9,
    color: primary.bluish1,
    fontFamily: 'ProximaNova-Bold',
  },
  focused: {
    backgroundColor: primary.bluish8,
  },
  innerWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  centeredContent: {
    justifyContent: 'center',
  },
  colorBox: {
    marginRight: spacing(2),
  },
  userLabel: {
    fontSize: 16,
    lineHeight: '20px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  attributeIcon: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: spacing(1),
    marginTop: spacing(-0.25),
    '& svg': {
      '& g, & path': {
        fill: primary.bluish1,
      },
    },
    '& g, & path': {
      fill: primary.bluish1,
    },
  },
  previewButton: {
    width: 24,
    height: 24,
  },
  optionIcon: {
    width: 20,
    height: 20,
  },
  removeIcon: {
    flexShrink: 0,
    width: 14,
    height: 14,
    marginLeft: spacing(1),
  },
  removeIconTrailing: {
    marginLeft: 'auto',
  },
}));

const Option = props => {
  const classes = useStyles();
  const { onClick, ...otherProps } = props.innerProps;
  const {
    translations,
    isOptionValueCentered,
    hasHash,
    hasColorBox,
    isUser,
    isIcon,
    isAttribute,
    isInlineMulti,
    allowEmptySelection,
  } = props.selectProps;

  const canDeselect =
    allowEmptySelection || (props.getValue()?.length ?? 0) > 1;
  const showRemoveIcon = isInlineMulti && props.isSelected && canDeselect;
  const handleOptionClick = event => {
    if (isInlineMulti && props.isSelected && !canDeselect) {
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };
  const optionInnerProps = {
    ...props.innerProps,
    onClick: handleOptionClick,
  };
  const Icon = props.children;
  return isIcon ? (
    <components.Option
      {...props}
      innerProps={optionInnerProps}
      className={classNames(classes.root, {
        [classes.disabled]: props.isDisabled,
        [classes.selected]: props.isSelected,
        [classes.focused]: props.isFocused && !props.isSelected,
        [classes.centeredContent]: isOptionValueCentered,
      })}
    >
      <div
        {...otherProps}
        ref={props.innerRef}
        className={classes.innerWrapper}
      >
        <Icon className={classes.optionIcon} />
        {props.isDisabled && !props.isSelected && props.data?.disabledLabel && (
          <div className={classes.disabledLabel}>
            {props.data.disabledLabel}
          </div>
        )}
        {showRemoveIcon && (
          <CloseIcon
            className={classNames(
              classes.removeIcon,
              classes.removeIconTrailing
            )}
          />
        )}
      </div>
    </components.Option>
  ) : (
    <components.Option
      {...props}
      innerProps={optionInnerProps}
      className={classNames(classes.root, {
        [classes.disabled]: props.isDisabled,
        [classes.selected]: props.isSelected,
        [classes.focused]: props.isFocused && !props.isSelected,
        [classes.centeredContent]: isOptionValueCentered,
      })}
    >
      <div
        {...otherProps}
        ref={props.innerRef}
        className={classes.innerWrapper}
      >
        {hasHash && props.data.id && <TagHash bgColor={props.data.color} />}
        {hasColorBox && (
          <ColorBox
            className={classes.colorBox}
            bgColor={props.data.color}
            isSmall
          />
        )}
        {isIcon && <Icon className={classes.optionIcon} />}
        {isUser && !props.data.shouldRemoveUserAvatar ? (
          <UserAvatar
            labelClass={classNames(classes.userLabel, {
              [classes.selected]: props.isSelected,
            })}
            user={props.data}
            caption
            small
          />
        ) : (
          <TextBoxWithTooltip
            className={classNames(classes.optionText, {
              [classes.disabled]: props.isDisabled,
              [classes.selected]: props.isSelected,
            })}
            text={props.children}
          />
        )}
        {props.isDisabled && !props.isSelected && props.data?.disabledLabel && (
          <div className={classes.disabledLabel}>
            {props.data.disabledLabel}
          </div>
        )}
        {isAttribute && (
          <div className={classes.iconWrapper}>
            {props.data.text_reply_questions && (
              <Tooltip
                customIconClass={classes.attributeIcon}
                text={translations.openEndedAttribute}
                icon={TextReplyIcon}
              />
            )}
            {props.data.is_private && (
              <Tooltip
                customIconClass={classes.attributeIcon}
                text={translations.personalAttribute}
                icon={PersonalAttributeIcon}
              />
            )}
          </div>
        )}
        {showRemoveIcon && (
          <CloseIcon
            className={classNames(classes.removeIcon, {
              [classes.removeIconTrailing]: !isAttribute,
            })}
          />
        )}
      </div>
    </components.Option>
  );
};

export default Option;
